import {
  put,
  call,
  take,
  race,
  delay,
  cancel,
  select,
  takeLatest,
  takeEvery,
  all
} from 'redux-saga/effects';
import {toast} from 'react-toastify';
import {
  RUN_GET_ACTIVE_ORDERS_JOB,
  SET_ACTIVE_ORDERS,
  SET_NEW_ORDERS,
  CHANGE_STATUS_ACTIVE,
  CHANGE_STATUS_ACTIVE_SUCCESS,
  STOP_GET_ACTIVE_ORDERS_JOB,
  SET_ACTIVE_EVENT_ORDERS,
  APPEND_LARGE_ORDERS,
  SET_LARGE_ORDER_INFO,
  FETCH_LARGE_ORDER_INFO,
  CREATE_LARGE_ORDER_DRIVERS,
  SET_LARGE_ORDER_SUBMITTING,
  REMOVE_LARGE_ORDER,
  APPEND_PENDING_NEW_ORDERS
} from 'store/global/ordersV2/action';
import {getActiveOrdersApi} from 'helpers/services/api/ordersV2';
import {
  createLargeDeliveryDriversApi,
  getLargeDeliveryInfoApi
} from 'helpers/services/api/largeDelivery';
import {
  ORDER,
  ORDER_CANCELLATION_STATUS,
  ORDER_SOURCE,
  ORDER_STATUS
} from 'helpers/constants/orders_v2';
import {changeStatus} from 'store/ordersV2/orderList/saga';
import {getStorageToken} from 'helpers/localStorage/authStorage';
import {errorLog, infoLog} from 'services/logger/new-logger';
import {OPEN_CANCELLATION_REFUND_MODAL} from 'store/global/ordersV2/CancellationRefund/action';
import {GET_BAD_ORDERS_BY_CORE} from 'store/ordersV2/orderDetails/action';
import {
  OPEN_CANCELLATION_CONFIRMATION_MODAL_V2,
  SET_LOADING_CANCELLATION_V2
} from 'store/global/modals/action';
import {hasLargeDeliveryAccess} from 'helpers/constants/restrictions';
import {isSauceDriverPending} from 'helpers/functionUtils/hybridDelivery';
import {
  buildLargeDeliveryCreatePayload,
  buildLargeDeliveryPreCheckPayload,
  checkIsFutureOrder,
  getLargeDeliveryTipCents,
  isLargeDeliveryOrder
} from 'helpers/functionUtils/ordersUtilsV2';
import {HANDLE_SUCCESS_MODAL} from 'store/bookADriver/bookADriverCreate/action';
import {BOOK_A_DRIVER_ERROR} from 'helpers/constants/bookAdriver';
import {shouldBlockBookADriverForClosedLocation} from 'helpers/functionUtils/bookADriver';

const GET_NEW_ORDERS_INTERVAL = 30000;

const currentActiveOrders = ({activeOrdersV2Reducer}) =>
  activeOrdersV2Reducer.activeOrders;

const currentLargeOrders = ({activeOrdersV2Reducer}) =>
  activeOrdersV2Reducer.largeOrders;

const currentNewOrders = ({activeOrdersV2Reducer}) =>
  activeOrdersV2Reducer.newOrders;

const userState = ({user}) => user;

export function* getActiveOrdersJob(stopNotificationSound) {
  while (true) {
    try {
      if (stopNotificationSound) {
        stopNotificationSound();
      }

      const activeLocation = yield select((state) => state.user.activeLocation);
      const {
        Timezone,
        _id: locationId,
        PickupReadyEnabled,
        AllowedServiceFeatures: serviceFeatures
      } = activeLocation;

      const {data} = yield getActiveOrdersApi(locationId);

      const currentOrders = yield select(currentActiveOrders);

      yield put(
        SET_ACTIVE_ORDERS({
          data: data || [],
          Timezone,
          PickupReadyEnabled,
          serviceFeatures
        })
      );

      infoLog(
        'ORDERS_POLLING_FRONTEND_SUCCESS',
        {
          Orders: data?.map((order) => order?.id)
        },
        yield select(userState)
      );

      const diff = currentOrders
        ? data?.filter(
            (newOrder) =>
              !currentOrders?.filter((order) => order.id === newOrder.id)[0] &&
              (!newOrder[ORDER.ORDER_STATUS] ||
                newOrder[ORDER.ORDER_STATUS] === ORDER_STATUS.NEW)
          )
        : [];

      if (diff.length > 0) {
        if (hasLargeDeliveryAccess(serviceFeatures, activeLocation)) {
          // While Sauce has yet to book the main driver the merchant can still
          // switch to their own, so no extra-drivers prompt yet; the details
          // banner offers it once the Sauce delivery exists.
          const offersExtraDrivers = (order) =>
            isLargeDeliveryOrder(order) &&
            !isSauceDriverPending(order, serviceFeatures);
          const largeOrders = diff.filter(offersExtraDrivers);
          const regularOrders = diff.filter(
            (order) => !offersExtraDrivers(order)
          );
          const queuedLargeOrders = yield select(currentLargeOrders);
          const openNewOrders = yield select(currentNewOrders);
          const isProcessingLargeOrders =
            queuedLargeOrders.length > 0 && !openNewOrders?.length;

          if (largeOrders.length > 0) {
            yield put(APPEND_LARGE_ORDERS(largeOrders));
            yield all(
              largeOrders.map((order) =>
                put(FETCH_LARGE_ORDER_INFO(order[ORDER.ID]))
              )
            );
          }

          if (isProcessingLargeOrders) {
            if (regularOrders.length > 0) {
              yield put(APPEND_PENDING_NEW_ORDERS(regularOrders));
            }
          } else {
            yield put(SET_NEW_ORDERS(diff));
          }
        } else {
          yield put(SET_NEW_ORDERS(diff));
        }
      }
    } catch (error) {
      errorLog(
        'ORDERS_POLLING_FRONTEND_ERROR',
        {},
        error,
        yield select(userState)
      );
    }

    yield delay(GET_NEW_ORDERS_INTERVAL);
  }
}

function* runGetActiveOrdersJob({payload}) {
  try {
    const isOnline = yield select(
      (state) => state.globalNotificationsReducer?.isNetworkOnline
    );
    const userData = yield select((state) => state.user.userData);
    const locationId = yield select((state) => state.user.activeLocation?._id);
    const {token} = getStorageToken();

    if (
      !!window.AndroidDashboard ||
      !isOnline ||
      !token ||
      !userData.role ||
      !locationId ||
      userData.role === 'sudo'
    ) {
      return;
    }

    const {stop} = yield race({
      job: call(() => getActiveOrdersJob(payload?.onPollAction)),
      stop: take(STOP_GET_ACTIVE_ORDERS_JOB)
    });

    if (stop) {
      yield cancel();
    }
  } catch (error) {
    errorLog(
      'ORDERS_POLLING_FRONTEND_ERROR',
      {},
      error,
      yield select(userState)
    );
    console.log('SAGA ERROR', error);
  }
}

export function* changeStatusActiveOrder({
  payload: {id, status, previousStatus},
  orderId = null
}) {
  try {
    yield put(SET_LOADING_CANCELLATION_V2(true));
    const data = yield changeStatus({payload: {id, status, previousStatus}});

    if (data && data?.order[ORDER.ID]) {
      if (data?.order[ORDER.ORDER_STATUS] === ORDER_STATUS.COMPLETED) {
        yield put(
          CHANGE_STATUS_ACTIVE_SUCCESS({[ORDER.ID]: data?.order[ORDER.ID]})
        );
      }
      if (data?.order[ORDER.ORDER_STATUS] === ORDER_STATUS.CANCELLED) {
        yield put(
          OPEN_CANCELLATION_CONFIRMATION_MODAL_V2({
            isOpen: false,
            info: null,
            source: null,
            deliveryId: null
          })
        );
        yield put(
          OPEN_CANCELLATION_REFUND_MODAL({
            isOpenModal: true,
            invoiceId:
              (!data?.order[ORDER.REFUND] ||
                !data?.order[ORDER.REFUND]?.find((el) => el.type === 'Full')) &&
              data?.order[ORDER.SOURCE] === ORDER_SOURCE.SAUCE
                ? data?.order[ORDER.INVOICE_ID]
                : null,
            showCannotDeliveryText:
              data?.orderProviderResult?.status &&
              data?.orderProviderResult?.status !==
                ORDER_CANCELLATION_STATUS.CANCELLED_SUCCESSFULLY,
            errorText:
              'Order has been cancelled. \nCannot cancel the delivery for this order.',
            orderId: data?.order[ORDER.ID]
          })
        );
        yield put(
          CHANGE_STATUS_ACTIVE_SUCCESS({[ORDER.ID]: data?.order[ORDER.ID]})
        );
      } else {
        yield put(
          CHANGE_STATUS_ACTIVE_SUCCESS({
            [ORDER.ORDER_STATUS]: data?.order[ORDER.ORDER_STATUS],
            [ORDER.ID]: data?.order[ORDER.ID]
          })
        );
      }
    }
    if (
      data &&
      typeof data?.orderProviderResult?.errorMessage === 'string' &&
      data?.order[ORDER.ORDER_STATUS] !== ORDER_STATUS.CANCELLED
    ) {
      toast.error(data?.orderProviderResult?.errorMessage);
    }
    yield put(SET_LOADING_CANCELLATION_V2(false));
    return data?.order[ORDER.ORDER_STATUS];
  } catch (error) {
    yield put(SET_LOADING_CANCELLATION_V2(false));
    if (status === ORDER_STATUS.CANCELLED) {
      yield put(
        OPEN_CANCELLATION_REFUND_MODAL({
          isOpenModal: true,
          invoiceId: null,
          showCannotDeliveryText: true,
          errorText: 'Something went wrong. Try again later'
        })
      );
    }
    console.log('SAGA ERROR', error);
    return false;
  }
}

function* fetchLargeOrderInfo({payload: orderId}) {
  try {
    const location = yield select((state) => state.user.activeLocation);
    const largeOrders = yield select(currentLargeOrders);
    const order = largeOrders.find(
      (largeOrder) => largeOrder[ORDER.ID] === orderId
    );

    if (!location?._id || !order) {
      yield put(SET_LARGE_ORDER_INFO({orderId, info: {}}));
      return;
    }

    const {data} = yield call(
      getLargeDeliveryInfoApi,
      buildLargeDeliveryPreCheckPayload(order, location)
    );
    const info = data?.data || data || {};
    yield put(SET_LARGE_ORDER_INFO({orderId, info}));
  } catch (error) {
    yield put(SET_LARGE_ORDER_INFO({orderId, info: {}}));
    errorLog(
      'LARGE_DELIVERY_INFO_ERROR',
      {orderId},
      error,
      yield select(userState)
    );
  }
}

function* createLargeOrderDrivers({payload}) {
  try {
    yield put(SET_LARGE_ORDER_SUBMITTING(true));
    const location = yield select((state) => state.user.activeLocation);
    const largeOrders = yield select(currentLargeOrders);
    const {orderId, additionalDrivers, tip, customTipAmount} = payload || {};
    const order = largeOrders.find(
      (largeOrder) => largeOrder[ORDER.ID] === orderId
    );

    if (!location?._id || !order) {
      yield put(SET_LARGE_ORDER_SUBMITTING(false));
      return;
    }

    if (shouldBlockBookADriverForClosedLocation(location)) {
      yield put(SET_LARGE_ORDER_SUBMITTING(false));
      toast.error(BOOK_A_DRIVER_ERROR.LOCATION_CLOSED);
      return;
    }

    const tipCents = getLargeDeliveryTipCents({
      tip,
      customTipAmount,
      subtotal: order[ORDER.TOTALS]?.locationSubtotal
    });

    const {data} = yield call(
      createLargeDeliveryDriversApi,
      buildLargeDeliveryCreatePayload({
        order,
        location,
        additionalDrivers,
        tipCents
      })
    );
    const createdOrder = data?.data || data;
    const requestedCouriers = Number(additionalDrivers || 1);

    yield put(
      HANDLE_SUCCESS_MODAL({
        openSuccessModal: true,
        successData: {
          deliveryType: checkIsFutureOrder(order) ? 'FutureDelivery' : 'ASAP',
          pickup_time: createdOrder?.s2e_meta?.pickup_eta,
          requestedCouriers,
          successfulDeliveries:
            createdOrder?.successful_deliveries ?? requestedCouriers
        }
      })
    );
    yield put(REMOVE_LARGE_ORDER(orderId));

    const detailsOrderId = yield select(
      (state) => state.orderDetailsV2Reducer?.order?.[ORDER.ID]
    );
    if (detailsOrderId === orderId) {
      yield put(GET_BAD_ORDERS_BY_CORE());
    }
  } catch (error) {
    yield put(SET_LARGE_ORDER_SUBMITTING(false));
    toast.error('Failed to add drivers. Please try again.');
    errorLog(
      'LARGE_DELIVERY_CREATE_DRIVERS_ERROR',
      {orderId: payload?.orderId},
      error,
      yield select(userState)
    );
  }
}

function* activeOrdersV2Saga() {
  yield takeLatest(RUN_GET_ACTIVE_ORDERS_JOB, runGetActiveOrdersJob);
  yield takeEvery(CHANGE_STATUS_ACTIVE, changeStatusActiveOrder);
  yield takeEvery(FETCH_LARGE_ORDER_INFO, fetchLargeOrderInfo);
  yield takeEvery(CREATE_LARGE_ORDER_DRIVERS, createLargeOrderDrivers);
}

export default activeOrdersV2Saga;
